import {
  type ChangeEvent,
  type ComponentProps,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { useAtomsToastManager } from '@opendoor/atoms/toast';
import { cn } from '@opendoor/atoms/utils';
import { IconBookmark, IconBookmarkFilled, IconCheck } from '@tabler/icons-react';

import type { PropertySearchQuery } from '../helpers/useSearchQueries';
import type { SavedSearchFieldsFragment } from './__generated__/saved-searches.zeus';
import {
  findMatchingSavedSearch,
  hasValidLocation,
  queryMatchesSavedSearch,
  toSavedSearchInput,
} from './mapping';
import { claimPendingSavedSearch, stashPendingSavedSearch } from './pendingSavedSearch';
import { formatSavedSearchName } from './presentation';
import { useSavedSearches } from './useSavedSearches';

export interface SaveSearchButtonProps {
  /**
   * The canonical filter shape for the search being saved. The parent must
   * merge `searchState.getCanonical()` with the active location (market,
   * zoneIds, or polygon vertices) before passing it in; `getCanonical()`
   * intentionally omits location.
   */
  query: PropertySearchQuery;
  /**
   * The saved search the current results came from, if any. Drives the
   * update-vs-save-new flow: while the page state still matches it the button
   * shows a quiet "Saved" state, and once it diverges the button offers to
   * update it in place instead of piling up near-duplicates.
   */
  activeSavedSearch?: SavedSearchFieldsFragment | null;
  /**
   * Display label for the active location (e.g. a selected neighborhood name),
   * used to build a better default name than the market slug.
   */
  locationLabel?: string | null;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  /**
   * The label that offers to save a new search.
   * @default 'Save search'
   */
  saveLabel?: string;
  /** Stretches the button to the width of its container. */
  fullWidth?: boolean;
  /** Why the button is disabled, added to its label, e.g. a filter saved searches can't keep. */
  disabledReason?: string;
  /**
   * `button` is a labeled button. `icon` is a bookmark icon button, as in the
   * map search bar: an outline to save, filled once the search is saved.
   * @default 'button'
   */
  appearance?: 'button' | 'icon';
  disabled?: boolean;
  onSaved?: (savedSearchId: string) => void;
  /**
   * Refetch the saved-search list when the button mounts. Turn it off where
   * the button mounts each time a panel opens. The list still loads when
   * nothing is cached, and a save loads it first when it has not loaded.
   * @default true
   */
  refetchSavedSearchesOnMount?: boolean;
}

const NO_LOCATION_HINT = 'Pick a market, neighborhood, or area on the map first.';
const GENERIC_SAVE_ERROR = 'Failed to save search. Please try again.';
const GENERIC_UPDATE_ERROR = 'Failed to update search. Please try again.';
const LIST_LOAD_ERROR = 'Couldn’t load your saved searches. Please try again.';
const SAVE_SEARCH_NAME_MAX_LENGTH = 80;

// The marketplace doesn't have an in-page login modal, so unauthenticated
// users are bounced to `/login?redirectTo=...` matching `useMarketplaceFavorite`.
// The pending search is stashed first so the save resumes after login.
const redirectToLogin = () => {
  const redirectTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/login?redirectTo=${encodeURIComponent(redirectTo)}`);
};

type SaveDialogState = { open: boolean; view: 'name' | 'choose' };

export const SaveSearchButton = ({
  query,
  activeSavedSearch,
  locationLabel,
  variant = 'secondary',
  size = 'lg',
  saveLabel = 'Save search',
  fullWidth = false,
  appearance = 'button',
  disabled = false,
  disabledReason,
  onSaved,
  refetchSavedSearchesOnMount = true,
}: SaveSearchButtonProps) => {
  const { isLoggedIn, savedSearches, hasLoaded, add, update, refetch } = useSavedSearches({
    refetchOnMount: refetchSavedSearchesOnMount,
  });
  const toastManager = useAtomsToastManager();
  // `useAtomsToastManager` returns a fresh object each render, so read it
  // through a ref instead of a dependency (matches CopyLinkToast.tsx et al.).
  const toastManagerRef = useRef(toastManager);
  // oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render
  toastManagerRef.current = toastManager;

  const [dialog, setDialog] = useState<SaveDialogState>({ open: false, view: 'name' });
  const triggerRef = useRef<HTMLElement | null>(null);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Set when submit found an equivalent existing search; the next submit
  // ("Save anyway") proceeds past the warning.
  const [duplicateOf, setDuplicateOf] = useState<SavedSearchFieldsFragment | null>(null);
  // A save resumed after the login bounce uses the query stashed pre-login,
  // not the (possibly SSR-reset) live page query.
  const [resumedQuery, setResumedQuery] = useState<PropertySearchQuery | null>(null);
  const setInputRef = useCallback((node: HTMLInputElement | null) => {
    node?.focus();
  }, []);
  const nameInputId = useId();

  const validLocation = hasValidLocation(query);
  const isDirty = !!activeSavedSearch && !queryMatchesSavedSearch(query, activeSavedSearch);
  // Only treat the page as cleanly matching once the list has loaded; otherwise
  // an empty pre-load list would falsely disable the button as "Saved".
  const isCleanActive = hasLoaded && !!activeSavedSearch && !isDirty;

  // Resume a save interrupted by the login bounce. The claim is destructive
  // (read-and-remove), so with several SaveSearchButton instances mounted only
  // one opens the dialog.
  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    const pending = claimPendingSavedSearch();
    if (!pending) {
      return;
    }
    // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
    setResumedQuery(pending.query);
    setName(pending.name);
    setErrorMessage(null);
    setDuplicateOf(null);
    setDialog({ open: true, view: 'name' });
  }, [isLoggedIn]);

  const handleOpenChange: NonNullable<ComponentProps<typeof Dialog.Root>['onOpenChange']> = (
    open,
    details,
  ) => {
    // The bookmark stays focusable while the search is saved, so it does nothing then.
    if (open && isCleanActive) {
      details.cancel();
      return;
    }
    if (!open) {
      if (submitting) {
        details.cancel();
        return;
      }
      if (details.reason === 'close-press' || details.reason === 'escape-key') {
        // Keep an enclosing dialog from reclaiming focus as this popup disappears.
        triggerRef.current?.focus({ preventScroll: true });
      }
      setDialog((current) => ({ ...current, open: false }));
      return;
    }
    if (details.trigger instanceof HTMLElement) triggerRef.current = details.trigger;
    const suggestedName = formatSavedSearchName(query, locationLabel);
    if (!isLoggedIn) {
      details.cancel();
      stashPendingSavedSearch({ name: suggestedName, query });
      redirectToLogin();
      return;
    }
    setResumedQuery(null);
    setErrorMessage(null);
    setDuplicateOf(null);
    if (!isDirty) setName(suggestedName);
    setDialog({ open: true, view: isDirty ? 'choose' : 'name' });
  };

  const handleSubmitNew = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName || submitting) {
      return;
    }
    const saveQuery = resumedQuery ?? query;

    setSubmitting(true);
    setErrorMessage(null);

    // Warn once when an equivalent search already exists (same location +
    // filters). Await a settled list first so a fast submit before the initial
    // saved-search query lands cannot skip duplicate detection. If the list still
    // has not loaded successfully, keep the dialog open rather than fail-open
    // against an unknown empty list. A second submit ("Save anyway") proceeds
    // past the warning.
    if (!duplicateOf) {
      let list = savedSearches;
      let listLoaded = hasLoaded;
      if (!listLoaded) {
        const result = await refetch();
        list = result.savedSearches;
        listLoaded = result.hasLoaded;
      }
      if (!listLoaded) {
        setErrorMessage(LIST_LOAD_ERROR);
        setSubmitting(false);
        return;
      }
      const existing = findMatchingSavedSearch(saveQuery, list);
      if (existing) {
        setDuplicateOf(existing);
        setSubmitting(false);
        return;
      }
    }

    const result = await add(toSavedSearchInput(saveQuery, trimmedName));
    setSubmitting(false);
    if (result.success) {
      toastManagerRef.current.add({ title: `Saved “${trimmedName}”`, variant: 'success' });
      setDialog((current) => ({ ...current, open: false }));
      onSaved?.(result.savedSearch?.id ?? '');
      return;
    }
    setErrorMessage(result.errorMessage ?? GENERIC_SAVE_ERROR);
  }, [
    add,
    duplicateOf,
    hasLoaded,
    name,
    onSaved,
    query,
    refetch,
    resumedQuery,
    savedSearches,
    submitting,
  ]);

  const handleUpdateActive = useCallback(async () => {
    if (!activeSavedSearch || submitting) {
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    const result = await update(
      activeSavedSearch.id,
      // The active record is the preservation source so fields Cosmos has no
      // UI for (max bed/bath, FHA, finite HOA cap) survive the update.
      toSavedSearchInput(query, activeSavedSearch.name, activeSavedSearch),
    );
    setSubmitting(false);
    if (result.success) {
      toastManagerRef.current.add({
        title: `Updated “${activeSavedSearch.name}”`,
        variant: 'success',
      });
      setDialog((current) => ({ ...current, open: false }));
      return;
    }
    setErrorMessage(result.errorMessage ?? GENERIC_UPDATE_ERROR);
  }, [activeSavedSearch, query, submitting, update]);

  const handleChooseSaveAsNew = useCallback(() => {
    setName(formatSavedSearchName(query, locationLabel));
    setErrorMessage(null);
    setDialog({ open: true, view: 'name' });
  }, [query, locationLabel]);

  const trimmedName = name.trim();
  const canSubmit = !submitting && trimmedName.length > 0;
  const triggerHint = validLocation ? undefined : NO_LOCATION_HINT;

  let submitLabel = duplicateOf ? 'Save anyway' : 'Save';
  if (submitting) {
    submitLabel = 'Saving…';
  }

  const triggerLabel = isDirty ? 'Update search' : saveLabel;
  let triggerAriaLabel = validLocation ? triggerLabel : `${triggerLabel}. ${triggerHint}`;
  if (disabled && disabledReason) triggerAriaLabel = `${triggerLabel}. ${disabledReason}`;
  if (isCleanActive) triggerAriaLabel = `Search saved as ${activeSavedSearch.name}`;
  const isIcon = appearance === 'icon';

  return (
    <Dialog.Root
      role="dialog"
      open={dialog.open}
      onOpenChange={handleOpenChange}
      disablePointerDismissal={submitting}
    >
      <Dialog.Trigger
        // A saved bookmark stays enabled so focus can come back to it when the
        // save dialog closes; it opens nothing (see handleOpenChange).
        disabled={(isCleanActive && !isIcon) || disabled || !validLocation}
        render={
          isIcon ? (
            <Button
              // A click on a saved bookmark opens nothing, so it is tracked apart.
              analyticsName={
                isCleanActive ? 'save-search-icon-button-saved' : 'save-search-icon-button'
              }
              size="sm"
              variant="secondary"
              aria-label={triggerAriaLabel}
              aria-disabled={isCleanActive || undefined}
              title={triggerAriaLabel}
              className={cn(
                // The variants match the atoms button's own, so these replace them.
                'group size-9 shrink-0 rounded-full border-0 bg-transparent p-0 shadow-none disabled:not-aria-busy:bg-transparent',
                isCleanActive
                  ? // A saved bookmark opens nothing, so it doesn't react to hover or press.
                    'cursor-default not-disabled:hover:bg-transparent not-disabled:active:scale-100 not-disabled:active:bg-transparent'
                  : 'not-disabled:hover:bg-od-bg-hover-tertiary',
              )}
            >
              {isCleanActive ? (
                <IconBookmarkFilled
                  size={24}
                  aria-hidden
                  className="text-od-content-accent-brand"
                />
              ) : (
                // The color sits on the icon: the button sets its own text color.
                <IconBookmark
                  size={24}
                  aria-hidden
                  className="text-[#A4A19B] transition-colors group-enabled:group-hover:text-od-content-primary group-disabled:opacity-50"
                />
              )}
            </Button>
          ) : (
            <Button
              analyticsName="save-search-button"
              size={size}
              variant={isCleanActive ? 'secondary' : variant}
              aria-label={triggerAriaLabel}
              className={cn('max-md:min-w-0 max-md:flex-1', fullWidth && 'w-full')}
            >
              {isCleanActive ? (
                <>
                  <IconCheck size={18} aria-hidden />
                  Saved
                </>
              ) : (
                triggerLabel
              )}
            </Button>
          )
        }
      />
      <Dialog title={dialog.view === 'choose' ? 'Update saved search?' : 'Name your saved search'}>
        {dialog.view === 'choose' ? (
          <UpdateSavedSearchContent
            submitting={submitting}
            activeSavedSearch={activeSavedSearch}
            errorMessage={errorMessage}
            onSaveAsNew={handleChooseSaveAsNew}
            onUpdateActive={handleUpdateActive}
          />
        ) : (
          <NameSavedSearchContent
            name={name}
            submitting={submitting}
            duplicateOf={duplicateOf}
            errorMessage={errorMessage}
            canSubmit={canSubmit}
            submitLabel={submitLabel}
            inputRef={setInputRef}
            nameInputId={nameInputId}
            onNameChange={setName}
            onSubmit={handleSubmitNew}
          />
        )}
      </Dialog>
    </Dialog.Root>
  );
};

interface UpdateSavedSearchContentProps {
  submitting: boolean;
  activeSavedSearch?: SavedSearchFieldsFragment | null;
  errorMessage: string | null;
  onSaveAsNew: () => void;
  onUpdateActive: () => void;
}

const UpdateSavedSearchContent = ({
  submitting,
  activeSavedSearch,
  errorMessage,
  onSaveAsNew,
  onUpdateActive,
}: UpdateSavedSearchContentProps) => (
  <>
    <Dialog.Description className="text-body-md text-od-content-secondary">
      {`Your filters have changed. Apply the changes to “${activeSavedSearch?.name}”, or keep it and save these filters as a new search.`}
    </Dialog.Description>
    {errorMessage && (
      <Text as="p" role="alert" className="pt-3 text-body-sm text-od-content-error">
        {errorMessage}
      </Text>
    )}
    <Row className="justify-end gap-2 pt-6 max-md:flex-wrap">
      <Button
        variant="secondary"
        size="md"
        aria-label="Save as new search"
        analyticsName="save-search-as-new"
        onClick={onSaveAsNew}
        disabled={submitting}
      >
        Save as new
      </Button>
      <Button
        variant="primary"
        size="md"
        aria-label={`Update ${activeSavedSearch?.name}`}
        analyticsName="save-search-update-active"
        onClick={onUpdateActive}
        disabled={submitting}
        loading={submitting}
      >
        Update
      </Button>
    </Row>
  </>
);

interface NameSavedSearchContentProps {
  name: string;
  submitting: boolean;
  duplicateOf: SavedSearchFieldsFragment | null;
  errorMessage: string | null;
  canSubmit: boolean;
  submitLabel: string;
  inputRef: (node: HTMLInputElement | null) => void;
  nameInputId: string;
  onNameChange: (name: string) => void;
  onSubmit: () => void;
}

const NameSavedSearchContent = ({
  name,
  submitting,
  duplicateOf,
  errorMessage,
  canSubmit,
  submitLabel,
  inputRef,
  nameInputId,
  onNameChange,
  onSubmit,
}: NameSavedSearchContentProps) => {
  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    onNameChange(event.target.value);
  };
  return (
    <>
      <Dialog.Description className="text-body-md text-od-content-secondary">
        Give your search a name so you can find it later.
      </Dialog.Description>
      <Col className="pt-4 pb-2">
        <Col className="gap-1.5">
          <Text as="label" htmlFor={nameInputId} className="text-label-sm text-od-content-primary">
            Search name
          </Text>
          <Input
            ref={inputRef}
            id={nameInputId}
            size="base"
            value={name}
            onChange={handleNameChange}
            maxLength={SAVE_SEARCH_NAME_MAX_LENGTH}
            analyticsName="save-search-name-input"
            placeholder="My saved search"
          />
        </Col>
        {duplicateOf && !errorMessage && (
          <Text as="p" role="alert" className="pt-2 text-body-sm text-od-content-secondary">
            {`You already have this search saved as “${duplicateOf.name}”.`}
          </Text>
        )}
        {errorMessage && (
          <Text as="p" role="alert" className="pt-2 text-body-sm text-od-content-error">
            {errorMessage}
          </Text>
        )}
      </Col>
      <Row className="justify-end gap-2 pt-6">
        <Dialog.Close
          disabled={submitting}
          render={
            <Button
              variant="secondary"
              size="md"
              aria-label="Cancel"
              analyticsName="save-search-cancel"
            />
          }
        >
          Cancel
        </Dialog.Close>
        <Button
          variant="primary"
          size="md"
          aria-label={submitLabel}
          analyticsName="save-search-submit"
          onClick={onSubmit}
          disabled={!canSubmit}
          loading={submitting}
        >
          {submitLabel}
        </Button>
      </Row>
    </>
  );
};
